// src/components/TrophyRoom/MyReferrals.js
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  Alert,
  Snackbar,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  TextField,
  Divider,
  Fade,
  Collapse
} from '@mui/material';
import {
  PersonAdd as ReferralIcon,
  Business as BusinessIcon,
  Star as StarIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import client from '../../API';
import { replaceConsoleError } from '../../Utils/toastNotifications';

/** Robust clipboard copy with Safari + legacy fallbacks */
async function copyTextToClipboard(text) {
  try {
    if (
      typeof navigator !== 'undefined' &&
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === 'function' &&
      typeof window !== 'undefined' &&
      window.isSecureContext &&
      typeof document !== 'undefined' &&
      document.hasFocus()
    ) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to legacy
  }

  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '-9999px';
    document.body.appendChild(ta);

    const selection = document.getSelection();
    const originalRange =
      selection && selection.rangeCount ? selection.getRangeAt(0) : null;

    ta.select();
    const ok = document.execCommand('copy');

    document.body.removeChild(ta);
    if (originalRange) {
      selection.removeAllRanges();
      selection.addRange(originalRange);
    }
    if (ok) return true;
  } catch {
    // fall through to prompt
  }

  window.prompt('Copy this invite link:', text);
  return false;
}

function InviteLinkCard({ accountType, onGenerate, onCopy, link, loading, copied }) {
  const isBrand = accountType === 'brand';
  const icon = isBrand ? <BusinessIcon /> : <StarIcon />;
  const title = isBrand ? 'Invite Brands' : 'Invite Creators';
  const description = isBrand 
    ? 'Companies looking to hire creators' 
    : 'Content creators looking to monetize';

  return (
    <Card sx={{ 
      height: '100%',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: 4
      }
    }}>
      <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          {icon}
          <Box>
            <Typography variant="h6" fontWeight="bold">
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        {!link ? (
          <Button
            fullWidth
            variant="contained"
            onClick={() => onGenerate(accountType)}
            disabled={loading}
            startIcon={<LinkIcon />}
            sx={{ borderRadius: 2, py: 1.5 }}
          >
            {loading ? 'Generating...' : 'Generate Link'}
          </Button>
        ) : (
          <Fade in={true}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                Your invite link:
              </Typography>
              <TextField
                fullWidth
                value={link}
                variant="outlined"
                size="small"
                InputProps={{
                  readOnly: true,
                  sx: { 
                    fontSize: '0.875rem',
                    fontFamily: 'monospace',
                    '& .MuiOutlinedInput-input': {
                      padding: '8px 12px'
                    }
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => onCopy(link, accountType)}
                        color={copied ? 'success' : 'primary'}
                        size="small"
                      >
                        {copied ? <CheckIcon /> : <CopyIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />
              <Button
                fullWidth
                variant="outlined"
                onClick={() => onGenerate(accountType)}
                size="small"
                sx={{ borderRadius: 1.5 }}
              >
                Generate New Link
              </Button>
            </Box>
          </Fade>
        )}
      </CardContent>
    </Card>
  );
}

function MyReferrals() {
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [inviteLinks, setInviteLinks] = useState({
    brand: '',
    creator: ''
  });
  const [loading, setLoading] = useState({
    brand: false,
    creator: false
  });
  const [copied, setCopied] = useState({
    brand: false,
    creator: false
  });

  const { data: referralData, isLoading: referralsLoading, refetch } = useQuery(
    ['my-referrals'],
    () => client.globalUsers.getReferrals(),
    { staleTime: 1000 * 60 * 5 }
  );

  const closeSnackbar = () => setSnackbar(prev => ({ ...prev, open: false }));

  const safePick = (obj, key) => obj?.[key] ?? obj?.data?.[key];

  const toast = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  const handleGenerateLink = async (accountType) => {
    try {
      setLoading(prev => ({ ...prev, [accountType]: true }));
      setCopied(prev => ({ ...prev, [accountType]: false }));
      
      const result = await client.globalUsers.copyInvite(accountType);
      const success = safePick(result, 'success');
      const inviteUrl = safePick(result, 'invite_url') || safePick(result, 'inviteUrl') || '';

      if (success && inviteUrl) {
        setInviteLinks(prev => ({ ...prev, [accountType]: inviteUrl }));
        toast(`${accountType === 'brand' ? 'Brand' : 'Creator'} invite link generated!`, 'success');
        refetch();
      } else {
        throw new Error(safePick(result, 'error') || 'Failed to generate invite link');
      }
    } catch (error) {
      replaceConsoleError('Error generating invite link', error);
      toast(error?.message || 'Failed to generate invite link', 'error');
    } finally {
      setLoading(prev => ({ ...prev, [accountType]: false }));
    }
  };

  const handleCopyLink = async (link, accountType) => {
    try {
      const success = await copyTextToClipboard(link);
      if (success) {
        setCopied(prev => ({ ...prev, [accountType]: true }));
        toast('Link copied to clipboard!', 'success');
        
        // Reset the copied state after 2 seconds
        setTimeout(() => {
          setCopied(prev => ({ ...prev, [accountType]: false }));
        }, 2000);
      } else {
        toast('Please copy the link manually', 'warning');
      }
    } catch (error) {
      replaceConsoleError('Error copying link', error);
      toast('Failed to copy link', 'error');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const d = new Date(dateString);
    return isNaN(d.getTime()) ? 'Never' : d.toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
      case 'cancelled':
      case 'canceled':
        return 'error';
      default:
        return 'default';
    }
  };

  const referrals = referralData?.referrals || [];
  const stats = {
    total: referralData?.total_referrals || 0,
    completed: referralData?.completed_referrals || 0,
    pending: referralData?.pending_referrals || 0
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom display="flex" alignItems="center" gap={2} mb={4}>
        <ReferralIcon sx={{ fontSize: 40 }} />
        My Referrals
      </Typography>

      {/* Invite Section */}
      <Paper sx={{ p: 4, mb: 4, borderRadius: 3 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Generate Invite Links
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={4}>
          Create custom invite links to share with potential brands or creators.
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <InviteLinkCard
              accountType="brand"
              link={inviteLinks.brand}
              loading={loading.brand}
              copied={copied.brand}
              onGenerate={handleGenerateLink}
              onCopy={handleCopyLink}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InviteLinkCard
              accountType="creator"
              link={inviteLinks.creator}
              loading={loading.creator}
              copied={copied.creator}
              onGenerate={handleGenerateLink}
              onCopy={handleCopyLink}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Stats Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 3 }}>
            <Typography variant="h2" color="primary.main" fontWeight="bold" sx={{ mb: 1 }}>
              {stats.total}
            </Typography>
            <Typography variant="h6" color="text.secondary">Total Referrals</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 3 }}>
            <Typography variant="h2" color="success.main" fontWeight="bold" sx={{ mb: 1 }}>
              {stats.completed}
            </Typography>
            <Typography variant="h6" color="text.secondary">Successful</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 3 }}>
            <Typography variant="h2" color="warning.main" fontWeight="bold" sx={{ mb: 1 }}>
              {stats.pending}
            </Typography>
            <Typography variant="h6" color="text.secondary">Pending</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Referral History */}
      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Typography variant="h6" sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider', fontWeight: 'bold' }}>
          Referral History
        </Typography>

        {referralsLoading ? (
          <Box textAlign="center" py={6}>
            <LinearProgress sx={{ width: 300, mx: 'auto', mb: 3, height: 6, borderRadius: 3 }} />
            <Typography color="text.secondary" variant="h6">Loading referrals...</Typography>
          </Box>
        ) : referrals.length === 0 ? (
          <Box textAlign="center" py={8}>
            <ReferralIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 3 }} />
            <Typography variant="h5" color="text.secondary" gutterBottom>
              No referrals yet
            </Typography>
            <Typography color="text.secondary" mb={4} variant="body1">
              Generate and share your invite links to start earning referral rewards!
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Referred User</TableCell>
                  <TableCell>Account Type</TableCell>
                  <TableCell>Date Referred</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date Completed</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {referrals.map((referral, index) => (
                  <TableRow key={referral.id || index}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar sx={{ width: 32, height: 32 }}>
                          {referral.first_name?.charAt(0) || referral.email?.charAt(0) || 'U'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="600">
                            {referral.first_name && referral.last_name
                              ? `${referral.first_name} ${referral.last_name}`
                              : referral.email}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {referral.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={referral.referral_type === 'company' ? 'Brand' : 'Creator'}
                        color={referral.referral_type === 'company' ? 'primary' : 'secondary'}
                        size="small"
                        icon={referral.referral_type === 'company' ? <BusinessIcon /> : <StarIcon />}
                      />
                    </TableCell>
                    <TableCell>{formatDate(referral.date_generated)}</TableCell>
                    <TableCell>
                      <Chip label={referral.status || 'pending'} color={getStatusColor(referral.status)} size="small" />
                    </TableCell>
                    <TableCell>{formatDate(referral.date_completed)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default MyReferrals;